import { useState } from 'react';
import { CALENDARS, type CalendarId, fromMinutes, toMinutes } from '@quadro/domain';
import { confirmAction } from '../../components/Confirm';
import { hud } from '../../components/Hud';
import { Options } from '../../components/Options';
import { Sheet, SheetFooter, useAutoFocus } from '../../components/Sheet';
import * as api from '../../data/api';
import { keys, useAction, useEvents } from '../../data/queries';
import { closeSheet } from '../../state/sheets';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';

export function EventSheet({ id, preset }: { id: string | null; preset?: { date?: string; start?: string; end?: string } }) {
  const events = useEvents().data;
  const existing = id ? events?.find(e => e.id === id) : null;
  if (id && !existing) return null;
  return <EventForm key={id ?? 'new'} id={id} initial={existing ?? {
    title: '', location: '', date: preset?.date ?? useUI.getState().selectedDay, start: preset?.start ?? '10:00', end: preset?.end ?? '11:00', calendar: 'team' as CalendarId,
  }} />;
}

function EventForm({ id, initial }: { id: string | null; initial: api.EventDraft }) {
  const ws = useWorkspace();
  const ed = ws.canWrite;
  const [e, setE] = useState<api.EventDraft>(() => ({ title: initial.title, location: initial.location, date: initial.date, start: initial.start, end: initial.end, calendar: initial.calendar }));
  const ref = useAutoFocus<HTMLInputElement>();
  const save = useAction(api.saveEvent, [keys.events]);
  const remove = useAction(api.deleteEvent, [keys.events]);
  const patch = (p: Partial<api.EventDraft>) => setE(x => ({ ...x, ...p }));

  async function submit() {
    if (!e.title.trim()) return;
    const start = e.start || '09:00';
    let end = e.end || start;
    if (end <= start) end = fromMinutes(Math.min(23 * 60 + 59, toMinutes(start) + 60));
    await save(id, { ...e, title: e.title.trim(), location: e.location.trim(), start, end });
    const ui = useUI.getState();
    ui.set({ refDate: e.date, selectedDay: e.date, hiddenCalendars: ui.hiddenCalendars.filter(x => x !== e.calendar) });
    closeSheet();
    if (!id) hud('Evento adicionado');
  }

  return (
    <Sheet onClose={closeSheet} label="Evento">
      <div className="sh-body" onKeyDown={ev => { if (ev.key === 'Enter' && (ev.target as HTMLElement).tagName === 'INPUT' && ed) submit(); }}>
        <h2>{id ? 'Evento' : 'Novo evento'}</h2>
        <fieldset disabled={!ed} style={{ border: 0, padding: 0, margin: 0 }}><div className="form">
          <label htmlFor="fTitulo">Título</label><input id="fTitulo" ref={ref} className="title-in" maxLength={120} value={e.title} onChange={x => patch({ title: x.target.value })} />
          <label htmlFor="fLocal">Local</label><input id="fLocal" placeholder="Sala ou link da chamada" value={e.location} onChange={x => patch({ location: x.target.value })} />
          <label htmlFor="fData">Dia</label><input type="date" id="fData" value={e.date} onChange={x => patch({ date: x.target.value })} />
          <label htmlFor="fIni">Horário</label>
          <div className="pair"><input type="time" id="fIni" aria-label="Início" value={e.start} onChange={x => patch({ start: x.target.value })} /><span className="muted">até</span><input type="time" aria-label="Término" value={e.end} onChange={x => patch({ end: x.target.value })} /></div>
          <label htmlFor="fCal">Calendário</label>
          <select id="fCal" value={e.calendar} onChange={x => patch({ calendar: x.target.value as CalendarId })}><Options items={CALENDARS.map(c => [c.id, c.name])} /></select>
        </div></fieldset>
      </div>
      <SheetFooter>
        {id && ed && <button className="btn link" onClick={() => confirmAction({ title: 'Excluir este evento?', text: `“${e.title}” será removido da agenda da equipe.`, action: 'Excluir', onConfirm: async () => { closeSheet(); await remove(id); hud('Evento excluído'); } })}>Excluir evento</button>}
        <span className="grow" />
        <button className="btn" onClick={closeSheet}>{ed ? 'Cancelar' : 'Fechar'}</button>
        {ed && <button className="btn primary" disabled={!e.title.trim()} onClick={submit}>{id ? 'Salvar' : 'Adicionar'}</button>}
      </SheetFooter>
    </Sheet>
  );
}
